import { SessionStatus } from '@efacity/common';
import { smallCircularProgressButtonStyle, CircularProgressButton, smallButtonStyle } from '@efacity/frontend-shared';
import { Session as EfacitySession, LightTooltip } from '@efacity/react-next-sc';
import Cancel from '@mui/icons-material/Cancel';
import CopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import { Box, IconButton, Typography } from '@mui/material';
import { FC } from 'react';
import SessionPublishButton from '../../components/Buttons/SessionPublishButton';

export interface SessionsTableActionsCellProps {
  session: EfacitySession;
  onCloneSession: (session: EfacitySession) => void;
  handleRestoreSession: (sessionId: string) => void;
  onShowCancellationModal: (sessionId: string) => void;
  onSessionStatusChanged: (sessionId: string, newStatus: SessionStatus) => void;
  sessionIdUnderUpdate: string | null;
}

const SessionsTableActionsCell: FC<SessionsTableActionsCellProps> = ({
  session,
  onCloneSession,
  handleRestoreSession,
  onSessionStatusChanged,
  onShowCancellationModal,
  sessionIdUnderUpdate
}) => {
  const isSessionCancelled = !!session?.cancelledBy;
  const isRowBusy = sessionIdUnderUpdate === session._id;

  return (
    <Box display="flex" alignItems="center" data-testid="actions-cell" style={{ width: 185, maxWidth: 185 }}>
      <LightTooltip
        title={
          <>
            <Typography>Clone existing Session</Typography>
            <Typography variant="caption">(session start date will be set to current date)</Typography>
            <Typography variant="caption">
              (published session will be cloned with <span style={{ fontWeight: 'bold' }}>unpublished</span> status)
            </Typography>
          </>
        }
      >
        <span>
          <IconButton
            onClick={() => onCloneSession(session)}
            color="primary"
            data-testid="clone-btn"
            style={{ padding: 0, marginLeft: 5, marginRight: 5 }}
          >
            <CopyOutlinedIcon />
          </IconButton>
        </span>
      </LightTooltip>

      <SessionPublishButton
        onClick={(sessionStatus: SessionStatus) => {
          onSessionStatusChanged(session._id, sessionStatus);
        }}
        disabled={isSessionCancelled}
        progress={isRowBusy}
        sessionStatus={session.status}
        style={smallButtonStyle}
      />
      {session.cancelledAt ? (
        <LightTooltip title={<Typography>Restore Session</Typography>}>
          <span>
            <CircularProgressButton
              onClick={() => {
                handleRestoreSession(session._id);
              }}
              variant="contained"
              color="primary"
              progress={isRowBusy}
              data-testid="restoreButtonId"
              style={smallCircularProgressButtonStyle}
            >
              <RestoreIcon />
            </CircularProgressButton>
          </span>
        </LightTooltip>
      ) : (
        <LightTooltip title={<Typography>Cancel Session</Typography>}>
          <span>
            <CircularProgressButton
              onClick={() => {
                onShowCancellationModal(session._id);
              }}
              variant="contained"
              color="primary"
              progress={isRowBusy}
              disabled={isSessionCancelled}
              data-testid="cancelButtonId"
              style={smallCircularProgressButtonStyle}
            >
              <Cancel />
            </CircularProgressButton>
          </span>
        </LightTooltip>
      )}
    </Box>
  );
};

export default SessionsTableActionsCell;
